// DESCRIPTION: Featured-image / banner uploader for the event wizard. Supports
// DESCRIPTION: click-to-browse via the WordPress media library and drag-and-drop
// DESCRIPTION: upload (with a local-data-URL fallback when the REST upload fails).
import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';

/**
 * Opens the WordPress media library modal.
 *
 * Accessed lazily so a missing `window.wp.media` at module load doesn't crash.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Function} onSelect Called with the selected attachment URL.
 *
 * @return {void}
 */
const openMediaLibrary = ( onSelect ) => {
	const wpMedia = window.wp?.media;
	if ( ! wpMedia ) {
		return;
	}

	const frame = wpMedia( {
		title: __( 'Select Banner Image', 'nemtly-booking' ),
		button: { text: __( 'Use this image', 'nemtly-booking' ) },
		multiple: false,
		library: { type: 'image' },
	} );

	frame.on( 'select', () => {
		const attachment = frame.state().get( 'selection' ).first().toJSON();
		if ( attachment && attachment.url ) {
			onSelect( attachment.url );
		}
	} );

	frame.open();
};

/**
 * BannerUploader — featured-image picker with drag-and-drop.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {string}   props.value    Current image URL ('' when unset).
 * @param {Function} props.onChange Called with the new URL, or '' on remove.
 *
 * @return {JSX.Element}
 */
const BannerUploader = ( { value, onChange } ) => {
	const [ isDragging, setIsDragging ] = useState( false );

	const handleDragOver = ( e ) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging( true );
	};

	const handleDragLeave = ( e ) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging( false );
	};

	const handleDrop = ( e ) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging( false );

		const files = e.dataTransfer?.files;
		if ( ! files || files.length === 0 ) {
			return;
		}

		const file = files[ 0 ];
		if ( ! file.type.startsWith( 'image/' ) ) {
			return;
		}

		// Upload via WP REST media endpoint.
		const formDataObj = new FormData();
		formDataObj.append( 'file', file );

		fetch( `${ window.wpApiSettings?.root || '/wp-json/' }wp/v2/media`, {
			method: 'POST',
			headers: {
				'X-WP-Nonce':
					window.wpApiSettings?.nonce ||
					window.nemtlyCreateEvent?.nonce ||
					'',
			},
			body: formDataObj,
		} )
			.then( ( res ) => res.json() )
			.then( ( data ) => {
				if ( data?.source_url ) {
					onChange( data.source_url );
				}
			} )
			.catch( () => {
				// Fallback: local preview if the upload fails.
				const reader = new FileReader();
				reader.onload = ( ev ) => onChange( ev.target.result );
				reader.readAsDataURL( file );
			} );
	};

	return (
		<div className="nb-form-row nb-mb-6">
			<label className="nb-block nb-text-sm nb-font-semibold nb-text-neutral-700 nb-mb-2">
				{ __( 'Banner', 'nemtly-booking' ) }
			</label>

			{ value ? (
				<div className="nemtly-image-preview nb-relative nb-rounded-lg nb-border nb-border-neutral-200 nb-overflow-hidden">
					<img
						src={ value }
						alt={ __( 'Event banner', 'nemtly-booking' ) }
						className="nb-w-full nb-max-h-[250px] nb-object-cover"
					/>
					<div className="nb-absolute nb-bottom-3 nb-right-3 nb-flex nb-gap-2">
						<button
							type="button"
							className="nemtly-button-secondary nemtly-button-sm"
							onClick={ () => openMediaLibrary( onChange ) }
						>
							{ __( 'Change', 'nemtly-booking' ) }
						</button>
						<button
							type="button"
							className="nemtly-button-danger nemtly-button-sm"
							onClick={ () => onChange( '' ) }
						>
							{ __( 'Remove', 'nemtly-booking' ) }
						</button>
					</div>
				</div>
			) : (
				<div
					className={ `nemtly-upload-dropzone nb-border-2 nb-border-dashed nb-rounded-lg nb-p-8 nb-text-center nb-transition-colors nb-cursor-pointer ${
						isDragging
							? 'nb-border-accent-500 nb-bg-accent-50'
							: 'nb-border-neutral-300 nb-bg-neutral-50 hover:nb-bg-neutral-200'
					}` }
					onClick={ () => openMediaLibrary( onChange ) }
					onDragOver={ handleDragOver }
					onDragEnter={ handleDragOver }
					onDragLeave={ handleDragLeave }
					onDrop={ handleDrop }
				>
					<svg
						className="nb-mx-auto nb-h-12 nb-w-12 nb-text-neutral-500 nb-mb-3"
						stroke="currentColor"
						fill="none"
						viewBox="0 0 48 48"
						aria-hidden="true"
					>
						<path
							d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					<p className="nb-text-sm nb-text-neutral-700">
						{ isDragging ? (
							__( 'Drop image here', 'nemtly-booking' )
						) : (
							<>
								{ __(
									'Drop your files here or',
									'nemtly-booking'
								) }{ ' ' }
								<span className="nb-text-neutral-700 nb-font-medium">
									{ __( 'browse', 'nemtly-booking' ) }
								</span>
							</>
						) }
					</p>
					<p className="nb-text-xs nb-text-neutral-500 nb-mt-1">
						{ __( 'Maximum size: 50MB', 'nemtly-booking' ) }
					</p>
				</div>
			) }
		</div>
	);
};

export default BannerUploader;
